import type { KyInstance } from 'ky';

import { convertToQueryString, ensureError, omit } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { MailingsPagedData } from './types/mailings-paged-data.type';
import type { PagedMailingQuery } from './types/paged-mailing-query.type';

/**
 * Retrieves a paginated list of mailings from the API.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query parameters for pagination and filtering mailings.
 * @param isExternal - Whether to use the external mailings endpoint. Defaults to `false`.
 * @returns A promise that resolves to an operation result containing paged mailing data.
 * @throws Throws an error if the request fails.
 */
export const getPagedMailings = async (instance: KyInstance, query: PagedMailingQuery, isExternal = false) => {
  try {
    // `filter` stays stripped — this wave wires server-side `sort` only. `sort` is an array,
    // so it is flattened into `sort[n].field` / `sort[n].direction` query params rather than
    // spread as-is.
    const { isClosed = false, sort, ...searchParams } = omit(query, 'filter');

    return await instance
      .get<OperationResultWithData<MailingsPagedData>>(
        isExternal ? Uri.mailing.getPagedExternal : Uri.mailing.getPaged,
        {
          searchParams: {
            ...searchParams,
            isClosed,
            ...convertToQueryString(sort, 'sort'),
          },
        }
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
